import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, FileUploadOutlined as FileUploadOutlinedIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';

/**
 * SectionHeader component for displaying section headers in the application
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the section
 * @param {string} props.subtitle - Optional subtitle for the section
 * @param {function} props.onUpload - Optional callback for upload button click
 * @param {function} props.onMore - Optional callback for more options button click
 * @param {React.ReactNode} props.actions - Optional additional action buttons
 * @returns {React.ReactElement} The SectionHeader component
 */
const SectionHeader = ({ title, subtitle, onUpload, onMore, actions }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '8px 16px', // Further reduced padding to match logo height
        background: 'white', // Simple white background
        borderBottom: '1px solid #EAECF0', // Bottom border only
      }}
    >
      {/* Title Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827', // Dark text color for light background
                fontFamily: 'Roboto',
                lineHeight: '1.171875em',
              }}
            >
              {title}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: '14px', color: '#667085' }} />
          </Box>
          {subtitle && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: '#667085', // Lighter text color for subtitle
                fontFamily: 'Roboto',
                lineHeight: '1.2em',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* Default Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {onUpload && (
            <IconButton
              onClick={onUpload}
              sx={{
                width: '32px',
                height: '32px',
                padding: '6px 8px',
                borderRadius: '8px',
                color: '#465366', // Dark icon color for light background
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)', // Darker hover background for light theme
                },
              }}
            >
              <FileUploadOutlinedIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
          {onMore && (
            <IconButton
              onClick={onMore}
              sx={{
                width: '32px',
                height: '32px',
                padding: '6px 8px',
                borderRadius: '8px',
                color: '#465366', // Dark icon color for light background
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)', // Darker hover background for light theme
                },
              }}
            >
              <MoreVertIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
        </Box>
        
        {/* Custom Action Buttons */}
        {actions && actions}
      </Box>
    </Box>
  );
};

export default SectionHeader;
